/**
 * Barra de ações em lote de `/admin/cartoes`.
 * A seleção fica no pai. Esta barra só dispara o callback da ação,
 * e a exclusão só segue depois da confirmação com o alcance visível.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Trash2 } from 'lucide-react';

import {
  LOTE_EXCLUIR_ALCANCE,
  LOTE_INDISPONIVEL,
  rotuloSelecao,
  type LoteBarProps,
} from './loteSelecao';

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60 ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-surface';

const BTN =
  'inline-flex h-10 w-auto shrink-0 items-center justify-center gap-2 whitespace-nowrap ' +
  'rounded-input px-4 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ' +
  FOCUS;

const BTN_NEUTRAL = 'border border-edge bg-surface font-medium text-ink hover:bg-surface-hover';

const BTN_DELETE =
  'border border-danger/40 bg-surface font-medium text-danger-ink hover:bg-danger/[0.08]';

const BTN_CONFIRM =
  'border border-transparent bg-danger font-semibold text-white hover:brightness-110 dark:text-canvas';

const FIELD =
  'h-10 rounded-input border border-edge bg-surface px-3 text-sm disabled:cursor-not-allowed ' +
  'disabled:opacity-60 ' +
  FOCUS;

function trapTab(event: ReactKeyboardEvent<HTMLDivElement>) {
  if (event.key !== 'Tab') return;
  const panel = event.currentTarget;
  const focusables = Array.from(panel.querySelectorAll<HTMLElement>('button:not([disabled])'));
  if (focusables.length === 0) return;
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;
  const leavingBackward = event.shiftKey && (active === first || !panel.contains(active));
  const leavingForward = !event.shiftKey && (active === last || !panel.contains(active));
  if (!leavingBackward && !leavingForward) return;
  event.preventDefault();
  (event.shiftKey ? last : first).focus();
}

function ExcluirLoteDialog({
  count,
  busy,
  onCancel,
  onConfirm,
}: {
  count: number;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const titleId = useId();
  const countId = useId();
  const alcanceId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    cancelRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused instanceof HTMLElement && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [onCancel]);

  const onPanelKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onCancel();
      return;
    }
    trapTab(event);
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={`${countId} ${alcanceId}`}
        onKeyDown={onPanelKeyDown}
        className="flex w-full max-w-md flex-col gap-3 rounded-modal border border-edge bg-surface p-4 text-sm text-ink shadow-none"
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-chip border border-danger/40 bg-danger/[0.12] text-danger-ink">
            <Trash2 size={16} aria-hidden="true" />
          </span>
          <h2 id={titleId} className="m-0 text-base font-semibold text-ink">
            Excluir os cartões selecionados?
          </h2>
        </div>
        <p id={countId} className="m-0">
          {rotuloSelecao(count)}
        </p>
        <p id={alcanceId} className="m-0">
          {LOTE_EXCLUIR_ALCANCE}
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className={`${BTN} ${BTN_NEUTRAL}`}
          >
            Cancelar
          </button>
          <button type="button" disabled={busy} onClick={onConfirm} className={`${BTN} ${BTN_CONFIRM}`}>
            Excluir
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function LoteBar({
  count,
  baralhos,
  busy,
  indisponivel,
  onMove,
  onAddTag,
  onDuplicate,
  onConfirmDelete,
}: LoteBarProps) {
  const selectionLabelId = useId();
  const barRef = useRef<HTMLElement>(null);
  const [destinationId, setDestinationId] = useState('');
  const [tag, setTag] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (count < 1) return;
    barRef.current?.scrollIntoView({ block: 'nearest' });
  }, [count]);

  const closeDialog = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  useEffect(() => {
    if (count === 0 || indisponivel) setConfirmOpen(false);
  }, [count, indisponivel]);

  const trimmedTag = tag.trim();
  const moveDisabled = busy || destinationId === '';
  const addTagDisabled = busy || trimmedTag === '';

  const move = () => {
    if (busy || indisponivel || destinationId === '') return;
    onMove(destinationId);
  };

  const addTag = () => {
    const next = tag.trim();
    if (busy || indisponivel || next === '') return;
    onAddTag(next);
  };

  const duplicate = () => {
    if (busy || indisponivel) return;
    onDuplicate();
  };

  const askDelete = () => {
    if (busy || indisponivel) return;
    setConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (busy || indisponivel) return;
    onConfirmDelete();
    setConfirmOpen(false);
  };

  if (count === 0) return null;

  const dialogVisible = confirmOpen && !indisponivel;

  return (
    <>
      <section
        aria-labelledby={selectionLabelId}
        ref={barRef}
        aria-busy={busy}
        className="flex flex-row flex-wrap items-center gap-2 rounded-card border border-edge bg-surface p-3 text-sm text-ink sm:gap-3"
      >
        <p id={selectionLabelId} className="m-0 w-full shrink-0 sm:w-auto" aria-live="polite">
          {rotuloSelecao(count)}
        </p>

        {indisponivel ? (
          <p className="m-0 min-w-0">{LOTE_INDISPONIVEL}</p>
        ) : (
          <>
            <div className="flex w-full min-w-0 flex-col gap-1 sm:w-auto">
              <span className="text-xs font-medium text-ink-secondary">Mover para outro baralho</span>
              <div className="flex min-w-0 flex-row items-center gap-2">
                <select
                  aria-label="Baralho de destino"
                  value={destinationId}
                  disabled={busy}
                  onChange={(event) => setDestinationId(event.target.value)}
                  className={`${FIELD} min-w-0 flex-1 sm:w-56 sm:flex-none ${destinationId === '' ? 'text-ink-muted' : 'text-ink'}`}
                >
                  <option value="">Escolher baralho</option>
                  {baralhos.map((baralho) => (
                    <option key={baralho.id} value={baralho.id}>
                      {baralho.name}
                    </option>
                  ))}
                </select>
                <button type="button" disabled={moveDisabled} onClick={move} className={`${BTN} ${BTN_NEUTRAL}`}>
                  Mover
                </button>
              </div>
            </div>

            <div className="flex w-full min-w-0 flex-row items-center gap-2 sm:w-auto">
              <input
                aria-label="Tag"
                placeholder="Tag"
                value={tag}
                disabled={busy}
                onChange={(event) => setTag(event.target.value)}
                className={`${FIELD} min-w-0 flex-1 placeholder:text-ink-muted sm:w-48 sm:flex-none`}
              />
              <button
                type="button"
                disabled={addTagDisabled}
                onClick={addTag}
                className={`${BTN} ${BTN_NEUTRAL}`}
              >
                Acrescentar tag
              </button>
            </div>

            <button type="button" disabled={busy} onClick={duplicate} className={`${BTN} ${BTN_NEUTRAL}`}>
              Duplicar
            </button>

            <button type="button" disabled={busy} onClick={askDelete} className={`${BTN} ${BTN_DELETE}`}>
              <Trash2 size={16} aria-hidden="true" />
              Excluir
            </button>
          </>
        )}
      </section>

      {dialogVisible ? (
        <ExcluirLoteDialog count={count} busy={busy} onCancel={closeDialog} onConfirm={confirmDelete} />
      ) : null}
    </>
  );
}
